import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductDetails, getRelatedProducts } from '../store/api/products';
import Loader from '../components/common/loader';
import AddToCartButton from '../components/cart/addToCartButton';
import WishlistButton from '../components/wishlist/wishlistButton';

// ─── Courier Bag Detail Sub-Component ─────────────────────────────────────────
const CourierBagDetail = ({ product }) => {
  const bagType = product.courierBagType || 'NON_POD';
  const sizeVariants = product.sizeVariants || [];

  const [selectedSize, setSelectedSize] = useState(
    sizeVariants.length > 0 ? sizeVariants[0] : null
  );
  const [hoveredSize, setHoveredSize] = useState(null);
  const [selectedPack, setSelectedPack] = useState(
    sizeVariants[0]?.packOptions?.[0] || null
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(
    sizeVariants[0]?.images?.find((i) => i.isDefault) ||
      sizeVariants[0]?.images?.[0] ||
      product.images?.find((i) => i.isDefault) ||
      product.images?.[0] ||
      null
  );

  useEffect(() => {
    const activeSize = hoveredSize || selectedSize;
    if (!activeSize) return;
    const img =
      activeSize.images?.find((i) => i.isDefault) ||
      activeSize.images?.[0] ||
      null;
    if (img) setSelectedImage(img);
  }, [hoveredSize, selectedSize]);

  const handleSizeClick = (variant) => {
    setSelectedSize(variant);
    setHoveredSize(null);
    setSelectedPack(variant.packOptions?.[0] || null);
    setQuantity(1);
  };

  const handlePackSelect = (pack) => {
    setSelectedPack(pack);
    setQuantity(1);
  };

  const totalPieces = selectedPack ? selectedPack.pieces * quantity : 0;
  const totalPrice = selectedPack ? selectedPack.pricePerPack * quantity : 0;
  const totalMrp = selectedPack
    ? (selectedPack.mrpPerPack || selectedPack.pricePerPack) * quantity
    : 0;
  const discountPct =
    totalMrp > totalPrice
      ? Math.round(((totalMrp - totalPrice) / totalMrp) * 100)
      : 0;

  const buildCartProduct = () => {
    if (!selectedSize || !selectedPack) return product;
      const variantStock = selectedSize.stock ?? selectedPack.stock ?? 999; // ← must be here, inside the function

    return {
      ...product,
      name: `${product.name} - ${selectedSize.size} (${bagType === 'POD' ? 'POD' : 'Non-POD'})`,
      sellingPrice: selectedPack.pricePerPack,
      price: selectedPack.pricePerPack,
      mrp: selectedPack.mrpPerPack || selectedPack.pricePerPack,
      minOrderQuantity: 1,
      imageUrl:
        selectedImage?.url ||
        selectedSize.images?.[0]?.url ||
        product.images?.[0]?.url,
      packPieces: selectedPack.pieces,
      totalPieces,
      sizeLabel: `${selectedSize.size}__${selectedPack.pieces}pcs`,
      stock: variantStock,
      stockAvailable: variantStock,
      isInStock: variantStock > 0,
      stockStatus: variantStock === 0 ? 'OUT_OF_STOCK' : variantStock <= 10 ? 'LOW_STOCK' : 'IN_STOCK',

    };
  };

  const displayImage =
    selectedImage?.url ||
    product.images?.find((i) => i.isDefault)?.url ||
    product.images?.[0]?.url ||
    '/images/placeholder.png';

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header bar */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-500 px-6 py-3 flex items-center gap-3">
        <span className="text-white font-bold text-lg tracking-wide">Courier Bags</span>
        <span
          className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
            bagType === 'POD'
              ? 'bg-green-300 text-green-900'
              : 'bg-yellow-300 text-yellow-900'
          }`}
        >
          {bagType === 'POD' ? '✓ POD — Proof of Delivery' : 'Non-POD'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* ── Image Column ───────────────────────────────────────────── */}
        <div className="p-6 bg-gray-50 flex flex-col items-center gap-4">
          {/* Main image */}
          <div className="w-full aspect-square max-w-sm rounded-2xl overflow-hidden border-2 border-gray-200 bg-white shadow-inner">
            <img
              src={displayImage}
              alt={(hoveredSize || selectedSize)?.size || product.name}
              className="w-full h-full object-contain transition-all duration-300"
            />
          </div>

          {/* Size thumbnail strip */}
          {sizeVariants.length > 1 && (
            <div className="flex gap-2 flex-wrap justify-center">
              {sizeVariants.map((v) => {
                const thumb =
                  v.images?.find((i) => i.isDefault)?.thumbnailUrl ||
                  v.images?.find((i) => i.isDefault)?.url ||
                  v.images?.[0]?.url;
                const isSelected = selectedSize?.size === v.size;
                const isHovered = hoveredSize?.size === v.size;
                return (
                  <button
                    key={v.size}
                    onMouseEnter={() => setHoveredSize(v)}
                    onMouseLeave={() => setHoveredSize(null)}
                    onClick={() => handleSizeClick(v)}
                    title={`Size: ${v.size}${v.label ? ` (${v.label})` : ''}`}
                    className={`relative w-16 h-16 rounded-xl border-2 overflow-hidden transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-600 ring-2 ring-blue-300 scale-105'
                        : isHovered
                        ? 'border-blue-400 scale-105'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {thumb ? (
                      <img src={thumb} alt={v.size} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-xs font-bold text-gray-600">
                        {v.size}
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute bottom-0 left-0 right-0 bg-blue-600 text-white text-[9px] text-center py-0.5 font-semibold">
                        Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Details Column ─────────────────────────────────────────── */}
        <div className="p-6 flex flex-col gap-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{product.name}</h1>
            {product.brand && (
              <p className="text-blue-600 text-sm mt-1 font-medium">by {product.brand}</p>
            )}
            {product.shortDescription && (
              <p className="text-gray-500 text-sm mt-2">{product.shortDescription}</p>
            )}
          </div>

          {/* ── Size Selector ─────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Size
              </h3>
              {(hoveredSize || selectedSize) && (
                <span className="text-sm font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md">
                  {(hoveredSize || selectedSize).size}
                  {(hoveredSize || selectedSize).label
                    ? ` — ${(hoveredSize || selectedSize).label}`
                    : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {sizeVariants.map((v) => {
                const isSelected = selectedSize?.size === v.size;
                const isHovered = hoveredSize?.size === v.size;
                return (
                  <button
                    key={v.size}
                    onMouseEnter={() => setHoveredSize(v)}
                    onMouseLeave={() => setHoveredSize(null)}
                    onClick={() => handleSizeClick(v)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                        : isHovered
                        ? 'border-blue-400 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    {v.size}
                    {v.label && (
                      <span className="ml-1 text-xs opacity-70">({v.label})</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Pack Options ──────────────────────────────────────── */}
          {selectedSize?.packOptions?.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                Pack Size
              </h3>
              <div className="flex flex-wrap gap-2">
                {selectedSize.packOptions.map((pack) => {
                  const isSelected = selectedPack?.pieces === pack.pieces;
                  const pricePerPiece = (pack.pricePerPack / pack.pieces).toFixed(2);
                  return (
                    <button
                      key={pack.pieces}
                      onClick={() => handlePackSelect(pack)}
                      className={`flex flex-col items-center px-4 py-3 rounded-2xl border-2 transition-all duration-200 min-w-[88px] ${
                        isSelected
                          ? 'border-orange-500 bg-orange-50 text-orange-900 shadow-md'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      <span className="font-extrabold text-xl leading-none">{pack.pieces}</span>
                      <span className="text-[11px] font-medium text-gray-500 mt-0.5">pieces</span>
                      <span className="font-bold text-sm mt-1">
                        ₹{pack.pricePerPack.toLocaleString('en-IN')}
                      </span>
                      <span className="text-[10px] text-gray-400 mt-0.5">
                        ₹{pricePerPiece}/pc
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Quantity (# of packs) ─────────────────────────────── */}
          {selectedPack && (
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                Number of Packs
              </h3>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center border-2 border-gray-300 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 font-bold text-xl transition"
                  >
                    −
                  </button>
                  <span className="w-12 text-center font-bold text-xl select-none">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 font-bold text-xl transition"
                  >
                    +
                  </button>
                </div>

                {/* Live total pieces counter */}
                {totalPieces > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2">
                    <p className="text-[11px] text-green-600 font-semibold uppercase tracking-wide">
                      Total Pieces
                    </p>
                    <p className="text-2xl font-extrabold text-green-800 leading-none mt-0.5">
                      {totalPieces.toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Price Summary ─────────────────────────────────────── */}
          {selectedPack && (
            <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
              <div className="flex items-end gap-3 flex-wrap">
                <span className="text-3xl font-extrabold text-gray-900">
                  ₹{totalPrice.toLocaleString('en-IN')}
                </span>
                {discountPct > 0 && (
                  <>
                    <span className="text-lg text-gray-400 line-through mb-0.5">
                      ₹{totalMrp.toLocaleString('en-IN')}
                    </span>
                    <span className="text-sm font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full mb-0.5">
                      {discountPct}% off
                    </span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {quantity} pack{quantity > 1 ? 's' : ''} × {selectedPack.pieces} pieces @ ₹{selectedPack.pricePerPack.toLocaleString('en-IN')}/pack
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                ≈ ₹{selectedPack.pieces > 0 ? (selectedPack.pricePerPack / selectedPack.pieces).toFixed(2) : '—'} per piece
              </p>
            </div>
          )}

          {/* ── Stock Status ──────────────────────────────────────── */}
          <div>
            {selectedSize?.isAvailable !== false && product.isInStock ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>In Stock
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>Out of Stock
              </span>
            )}
          </div>

          {/* ── Actions ───────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <AddToCartButton
                product={buildCartProduct()}
                quantity={quantity}
                showQuantity={false}
                fullWidth={true}
              />
            </div>
            <button className="border-2 border-gray-300 text-gray-700 py-3 px-5 rounded-xl hover:bg-gray-50 transition flex items-center justify-center gap-2">
              <WishlistButton productId={product.productId} showText={true} />
            </button>
          </div>

          {/* ── Meta ──────────────────────────────────────────────── */}
          <div className="text-xs text-gray-500 space-y-1 pt-3 border-t border-gray-100">
            {product.productId && (
              <p><span className="font-semibold text-gray-600">SKU:</span> {product.productId}</p>
            )}
            {product.material && (
              <p><span className="font-semibold text-gray-600">Material:</span> {product.material.replace(/_/g, ' ')}</p>
            )}
            {product.category && (
              <p>
                <span className="font-semibold text-gray-600">Category:</span>{' '}
                <Link to={`/category/${product.category}`} className="text-blue-600 hover:underline">
                  {product.category.replace(/_/g, ' ')}
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// ─── Paper Bag Detail Sub-Component ───────────────────────────────────────────
const PaperBagDetail = ({ product }) => {
  const paperBagTypeMap = {
    SOS_PAPER_BAGS:   { label: 'SOS Paper Bags',   badge: 'bg-amber-200 text-amber-900' },
    BROWN_PAPER_BAGS: { label: 'Brown Paper Bags',  badge: 'bg-yellow-700 text-yellow-50' },
    WHITE_PAPER_BAGS: { label: 'White Paper Bags',  badge: 'bg-gray-100 text-gray-800 border border-gray-300' },
  };
  const bagTypeMeta = paperBagTypeMap[product.paperBagType] || { label: 'Paper Bags', badge: 'bg-green-100 text-green-800' };
  const sizeVariants = product.sizeVariants || [];

  const [selectedSize, setSelectedSize] = useState(
    sizeVariants.length > 0 ? sizeVariants[0] : null
  );
  const [hoveredSize, setHoveredSize] = useState(null);
  const [selectedPack, setSelectedPack] = useState(
    sizeVariants[0]?.packOptions?.[0] || null
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(
    sizeVariants[0]?.images?.find((i) => i.isDefault) ||
      sizeVariants[0]?.images?.[0] ||
      product.images?.find((i) => i.isDefault) ||
      product.images?.[0] ||
      null
  );

  useEffect(() => {
    const activeSize = hoveredSize || selectedSize;
    if (!activeSize) return;
    const img =
      activeSize.images?.find((i) => i.isDefault) ||
      activeSize.images?.[0] ||
      null;
    if (img) setSelectedImage(img);
  }, [hoveredSize, selectedSize]);

  const handleSizeClick = (variant) => {
    setSelectedSize(variant);
    setHoveredSize(null);
    setSelectedPack(variant.packOptions?.[0] || null);
    setQuantity(1);
  };

  const handlePackSelect = (pack) => {
    setSelectedPack(pack);
    setQuantity(1);
  };

  const totalPieces = selectedPack ? selectedPack.pieces * quantity : 0;
  const totalPrice  = selectedPack ? selectedPack.pricePerPack * quantity : 0;
  const totalMrp    = selectedPack ? (selectedPack.mrpPerPack || selectedPack.pricePerPack) * quantity : 0;
  const discountPct = totalMrp > totalPrice
    ? Math.round(((totalMrp - totalPrice) / totalMrp) * 100)
    : 0;

  const buildCartProduct = () => {
    if (!selectedSize || !selectedPack) return product;
    return {
      ...product,
      name: `${product.name} - ${selectedSize.size} (${bagTypeMeta.label})`,
      sellingPrice: selectedPack.pricePerPack,
      price: selectedPack.pricePerPack,
      mrp: selectedPack.mrpPerPack || selectedPack.pricePerPack,
      minOrderQuantity: 1,
      imageUrl:
        selectedImage?.url ||
        selectedSize.images?.[0]?.url ||
        product.images?.[0]?.url,
      packPieces: selectedPack.pieces,
      totalPieces,
      sizeLabel: `${selectedSize.size}__${selectedPack.pieces}pcs`,

    };
  };

  const displayImage =
    selectedImage?.url ||
    product.images?.find((i) => i.isDefault)?.url ||
    product.images?.[0]?.url ||
    '/images/placeholder.png';

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header bar */}
      <div className="bg-gradient-to-r from-green-700 to-green-500 px-6 py-3 flex items-center gap-3">
        <span className="text-white font-bold text-lg tracking-wide">Paper Bags</span>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${bagTypeMeta.badge}`}>
          🛍️ {bagTypeMeta.label}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* ── Image Column ─────────────────────────────────────────── */}
        <div className="p-6 bg-gray-50 flex flex-col items-center gap-4">
          <div className="w-full aspect-square max-w-sm rounded-2xl overflow-hidden border-2 border-gray-200 bg-white shadow-inner">
            <img
              src={displayImage}
              alt={(hoveredSize || selectedSize)?.size || product.name}
              className="w-full h-full object-contain transition-all duration-300"
            />
          </div>

          {sizeVariants.length > 1 && (
            <div className="flex gap-2 flex-wrap justify-center">
              {sizeVariants.map((v) => {
                const thumb =
                  v.images?.find((i) => i.isDefault)?.thumbnailUrl ||
                  v.images?.find((i) => i.isDefault)?.url ||
                  v.images?.[0]?.url;
                const isSelected = selectedSize?.size === v.size;
                const isHovered  = hoveredSize?.size === v.size;
                return (
                  <button
                    key={v.size}
                    onMouseEnter={() => setHoveredSize(v)}
                    onMouseLeave={() => setHoveredSize(null)}
                    onClick={() => handleSizeClick(v)}
                    title={`Size: ${v.size}${v.label ? ` (${v.label})` : ''}`}
                    className={`relative w-16 h-16 rounded-xl border-2 overflow-hidden transition-all duration-200 ${
                      isSelected
                        ? 'border-green-600 ring-2 ring-green-300 scale-105'
                        : isHovered
                        ? 'border-green-400 scale-105'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    {thumb ? (
                      <img src={thumb} alt={v.size} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-xs font-bold text-gray-600">
                        {v.size}
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute bottom-0 left-0 right-0 bg-green-600 text-white text-[9px] text-center py-0.5 font-semibold">
                        Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Details Column ────────────────────────────────────────── */}
        <div className="p-6 flex flex-col gap-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{product.name}</h1>
            {product.brand && (
              <p className="text-green-600 text-sm mt-1 font-medium">by {product.brand}</p>
            )}
            {product.shortDescription && (
              <p className="text-gray-500 text-sm mt-2">{product.shortDescription}</p>
            )}
          </div>

          {/* ── Size Selector ──────────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Size</h3>
              {(hoveredSize || selectedSize) && (
                <span className="text-sm font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-md">
                  {(hoveredSize || selectedSize).size}
                  {(hoveredSize || selectedSize).label ? ` — ${(hoveredSize || selectedSize).label}` : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {sizeVariants.map((v) => {
                const isSelected = selectedSize?.size === v.size;
                const isHovered  = hoveredSize?.size === v.size;
                return (
                  <button
                    key={v.size}
                    onMouseEnter={() => setHoveredSize(v)}
                    onMouseLeave={() => setHoveredSize(null)}
                    onClick={() => handleSizeClick(v)}
                    className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all duration-200 ${
                      isSelected
                        ? 'border-green-600 bg-green-600 text-white shadow-md'
                        : isHovered
                        ? 'border-green-400 bg-green-50 text-green-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50'
                    }`}
                  >
                    {v.size}
                    {v.label && <span className="ml-1 text-xs opacity-70">({v.label})</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Pack Options ───────────────────────────────────────── */}
          {selectedSize?.packOptions?.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Pack Size</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSize.packOptions.map((pack) => {
                  const isSelected    = selectedPack?.pieces === pack.pieces;
                  const pricePerPiece = (pack.pricePerPack / pack.pieces).toFixed(2);
                  return (
                    <button
                      key={pack.pieces}
                      onClick={() => handlePackSelect(pack)}
                      className={`flex flex-col items-center px-4 py-3 rounded-2xl border-2 transition-all duration-200 min-w-[88px] ${
                        isSelected
                          ? 'border-orange-500 bg-orange-50 text-orange-900 shadow-md'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      <span className="font-extrabold text-xl leading-none">{pack.pieces}</span>
                      <span className="text-[11px] font-medium text-gray-500 mt-0.5">pieces</span>
                      <span className="font-bold text-sm mt-1">₹{pack.pricePerPack.toLocaleString('en-IN')}</span>
                      <span className="text-[10px] text-gray-400 mt-0.5">₹{pricePerPiece}/pc</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Quantity (# of packs) ─────────────────────────────── */}
          {selectedPack && (
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Number of Packs</h3>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center border-2 border-gray-300 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 font-bold text-xl transition"
                  >−</button>
                  <span className="w-12 text-center font-bold text-xl select-none">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 font-bold text-xl transition"
                  >+</button>
                </div>
                {totalPieces > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2">
                    <p className="text-[11px] text-green-600 font-semibold uppercase tracking-wide">Total Pieces</p>
                    <p className="text-2xl font-extrabold text-green-800 leading-none mt-0.5">
                      {totalPieces.toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Price Summary ─────────────────────────────────────── */}
          {selectedPack && (
            <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
              <div className="flex items-end gap-3 flex-wrap">
                <span className="text-3xl font-extrabold text-gray-900">
                  ₹{totalPrice.toLocaleString('en-IN')}
                </span>
                {discountPct > 0 && (
                  <>
                    <span className="text-lg text-gray-400 line-through mb-0.5">
                      ₹{totalMrp.toLocaleString('en-IN')}
                    </span>
                    <span className="text-sm font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full mb-0.5">
                      {discountPct}% off
                    </span>
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {quantity} pack{quantity > 1 ? 's' : ''} × {selectedPack.pieces} pieces @ ₹{selectedPack.pricePerPack.toLocaleString('en-IN')}/pack
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                ≈ ₹{selectedPack.pieces > 0 ? (selectedPack.pricePerPack / selectedPack.pieces).toFixed(2) : '—'} per piece
              </p>
            </div>
          )}

          {/* ── Stock Status ──────────────────────────────────────── */}
          <div>
            {selectedSize?.isAvailable !== false && product.isInStock ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>In Stock
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>Out of Stock
              </span>
            )}
          </div>

          {/* ── Actions ───────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <AddToCartButton
                product={buildCartProduct()}
                quantity={quantity}
                showQuantity={false}
                fullWidth={true}
              />
            </div>
            <button className="border-2 border-gray-300 text-gray-700 py-3 px-5 rounded-xl hover:bg-gray-50 transition flex items-center justify-center gap-2">
              <WishlistButton productId={product.productId} showText={true} />
            </button>
          </div>

          {/* ── Meta ──────────────────────────────────────────────── */}
          <div className="text-xs text-gray-500 space-y-1 pt-3 border-t border-gray-100">
            {product.productId && (
              <p><span className="font-semibold text-gray-600">SKU:</span> {product.productId}</p>
            )}
            {product.material && (
              <p><span className="font-semibold text-gray-600">Material:</span> {product.material.replace(/_/g, ' ')}</p>
            )}
            {product.category && (
              <p>
                <span className="font-semibold text-gray-600">Category:</span>{' '}
                <Link to={`/category/${product.category}`} className="text-blue-600 hover:underline">
                  {product.category.replace(/_/g, ' ')}
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main ProductDetail Component ─────────────────────────────────────────────
const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);
        const response = await getProductDetails(productId);
        const p = response.data.product;
        setProduct(p);
        setQuantity(p.minOrderQuantity || 1);
        if (p.colors?.length > 0) setSelectedColor(p.colors[0].name);
        if (p.images?.length > 0) {
          setSelectedImage(p.images.find((img) => img.isDefault) || p.images[0]);
        }
        const relatedResponse = await getRelatedProducts(productId);
        setRelatedProducts(relatedResponse.data.products || []);
        setLoading(false);
      } catch (err) {
        setError('Failed to load product details. Please try again.');
        setLoading(false);
      }
    };
    fetchProductData();
  }, [productId]);

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    if (product?.imagesByColor?.[color]?.length > 0) {
      setSelectedImage(product.imagesByColor[color][0]);
    }
  };

  const handleImageSelect = (image) => setSelectedImage(image);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (value > 0) setQuantity(value);
  };

  const incrementQuantity = () => setQuantity((prev) => prev + 1);

  const decrementQuantity = () => {
    const minQty = product?.minOrderQuantity || 1;
    if (quantity > minQty) setQuantity((prev) => prev - 1);
  };

  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <Loader size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 text-red-700 p-4 rounded mb-4">{error}</div>
        <Link to="/products" className="text-blue-600 hover:underline">← Back to products</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-100 text-yellow-800 p-4 rounded mb-4">Product not found.</div>
        <Link to="/products" className="text-blue-600 hover:underline">← Back to products</Link>
      </div>
    );
  }

  const minQty = product.minOrderQuantity || 1;
  const isCourierBagWithVariants =
    product.subCategory === 'COURIER_BAGS' && product.sizeVariants?.length > 0;
  const isPaperBagWithVariants =
    product.subCategory === 'PAPER_BAGS' && product.sizeVariants?.length > 0;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Breadcrumbs */}
      <div className="mb-6">
        <nav className="text-sm">
          <ol className="list-none p-0 inline-flex flex-wrap gap-1 items-center">
            {[
              { to: '/', label: 'Home' },
              { to: '/products', label: 'Products' },
              product.category
                ? {
                    to: `/category/${product.category}`,
                    label: product.category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                  }
                : null,
            ]
              .filter(Boolean)
              .map((crumb, idx) => (
                <li key={idx} className="flex items-center">
                  <Link to={crumb.to} className="text-gray-500 hover:text-blue-600">
                    {crumb.label}
                  </Link>
                  <svg className="w-3 h-3 mx-2 text-gray-400" fill="currentColor" viewBox="0 0 12 12">
                    <path d="M9.707 5.293l-5-5a1 1 0 00-1.414 1.414L7.586 6 3.293 10.293a1 1 0 101.414 1.414l5-5a1 1 0 000-1.414z" />
                  </svg>
                </li>
              ))}
            <li>
              <span className="text-gray-700 font-medium">{product.name}</span>
            </li>
          </ol>
        </nav>
      </div>

   {/* ── COURIER BAG LAYOUT ─────────────────────────────────────── */}
      {isCourierBagWithVariants ? (
        <>
          <CourierBagDetail product={product} />
          {(product.description || (product.specifications && Object.keys(product.specifications).length > 0)) && (
            <div className="mt-6 bg-white rounded-xl shadow-md p-6">
              {product.description && (
                <div className="mb-8">
                  <h2 className="text-xl font-bold mb-4">Product Description</h2>
                  <div className="prose max-w-none">
                    {product.description.split('\n').map((paragraph, index) =>
                      paragraph && <p key={index} className="mb-4 text-gray-700">{paragraph}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : isPaperBagWithVariants ? (
        <>
          <PaperBagDetail product={product} />
          {(product.description || (product.specifications && Object.keys(product.specifications).length > 0)) && (
            <div className="mt-6 bg-white rounded-xl shadow-md p-6">
              {product.description && (
                <div className="mb-8">
                  <h2 className="text-xl font-bold mb-4">Product Description</h2>
                  <div className="prose max-w-none">
                    {product.description.split('\n').map((paragraph, index) =>
                      paragraph && <p key={index} className="mb-4 text-gray-700">{paragraph}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* ── STANDARD LAYOUT ──────────────────────────────────────── */
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Images */}
            <div className="p-6">
              <div className="mb-4 h-80 overflow-hidden rounded-lg border">
                <img
                  src={selectedImage?.url || product.images?.[0]?.url || '/images/placeholder.png'}
                  alt={selectedImage?.alt || product.name}
                  className="w-full h-full object-contain"
                />
              </div>
              {product.images?.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {product.images.map((image, index) => (
                    <div
                      key={index}
                      className={`border rounded-md overflow-hidden cursor-pointer ${
                        selectedImage?.url === image.url ? 'border-blue-500' : 'border-gray-200'
                      }`}
                      onClick={() => handleImageSelect(image)}
                    >
                      <img
                        src={image.thumbnailUrl || image.url}
                        alt={image.alt || `${product.name} view ${index + 1}`}
                        className="w-full h-16 object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
              {product.brand && (
                <div className="mb-4">
                  <Link to={`/brand/${product.brand.toLowerCase()}`} className="text-blue-600 hover:underline">
                    {product.brand}
                  </Link>
                </div>
              )}

              {/* Price */}
              <div className="flex items-end mb-4">
                <span className="text-2xl font-bold text-gray-900">
                  ₹{product.price || product.sellingPrice || 0}
                </span>
                {product.mrp > (product.price || product.sellingPrice) && (
                  <>
                    <span className="text-lg text-gray-500 line-through ml-2">₹{product.mrp}</span>
                    <span className="text-md text-green-600 ml-2">
                      {calculateDiscount(product.mrp, product.price || product.sellingPrice)}% off
                    </span>
                  </>
                )}
              </div>

              {/* Availability */}
              <div className="mb-4">
                {product.isInStock ? (
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-sm">In Stock</span>
                ) : (
                  <span className="inline-block px-2 py-1 bg-red-100 text-red-800 rounded text-sm">Out of Stock</span>
                )}
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <h3 className="text-gray-700 font-medium mb-2">Quantity</h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border rounded-md">
                    <button
                      onClick={decrementQuantity}
                      disabled={quantity <= minQty}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition font-medium"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={handleQuantityChange}
                      min={minQty}
                      className="w-14 text-center border-x py-2 focus:outline-none"
                    />
                    <button
                      onClick={incrementQuantity}
                      className="px-3 py-2 text-gray-600 hover:bg-gray-100 transition font-medium"
                    >
                      +
                    </button>
                  </div>
                  {minQty > 1 && quantity < minQty && (
                    <span className="text-xs text-red-500">Min order: {minQty} units</span>
                  )}
                  {minQty > 1 && quantity >= minQty && (
                    <span className="text-xs text-green-600">✓ Meets minimum order of {minQty} units</span>
                  )}
                </div>
              </div>

              {/* Colors */}
              {product.colors?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-gray-700 font-medium mb-2">Colors</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((color) => (
                      <button
                        key={color.name}
                        className={`w-8 h-8 rounded-full border-2 ${
                          selectedColor === color.name ? 'border-blue-500' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.code }}
                        onClick={() => handleColorSelect(color.name)}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2 mb-6">
                <div className="w-full">
                  <AddToCartButton
                    product={product}
                    quantity={quantity}
                    color={selectedColor}
                    showQuantity={false}
                    fullWidth={true}
                  />
                </div>
                <button className="border border-gray-300 text-gray-700 py-3 px-6 rounded-md hover:bg-gray-100 transition flex items-center justify-center">
                  <WishlistButton productId={product.productId} showText={true} />
                </button>
              </div>

              {/* Short Description */}
              <div className="mb-6">
                <p className="text-gray-600">
                    {product.shortDescription || 'No description available'}

                </p>
              </div>

              {/* Misc */}
              <div className="flex flex-col gap-2 text-sm text-gray-600">
                {product.category && (
                  <p>
                    <span className="font-medium">Category: </span>
                    <Link to={`/category/${product.category}`} className="text-blue-600 hover:underline">
                      {product.category.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  </p>
                )}
                {product.subCategory && (
                  <p>
                    <span className="font-medium">Sub-Category: </span>
                    <Link to={`/subcategory/${product.subCategory}`} className="text-blue-600 hover:underline">
                      {product.subCategory.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </Link>
                  </p>
                )}
                {product.productId && (
                  <p><span className="font-medium">SKU: </span>{product.productId}</p>
                )}
              </div>
            </div>
          </div>

          {/* Description & Specs */}
          <div className="border-t">
            <div className="p-6">
              {product.description && (
                <div className="mb-8">
                  <h2 className="text-xl font-bold mb-4">Product Description</h2>
                  <div className="prose max-w-none">
                    {product.description.split('\n').map(
                      (paragraph, index) =>
                        paragraph && (
                          <p key={index} className="mb-4 text-gray-700">{paragraph}</p>
                        )
                    )}
                  </div>
                </div>
              )}
              {product.specifications &&
                typeof product.specifications === 'object' &&
                Object.keys(product.specifications).length > 0 && (
                  <div>
                    <h2 className="text-xl font-bold mb-4">Specifications</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <div key={key} className="flex border-b border-gray-200 py-2">
                          <span className="font-medium w-1/2">{key}</span>
                          <span className="w-1/2">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Related Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {relatedProducts.map((rp) => (
              <div key={rp.productId} className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition">
                <Link to={`/product/${rp.productId}`}>
                  <div className="h-48 overflow-hidden">
                    <img
                      src={rp.imageUrl || '/images/placeholder.png'}
                      alt={rp.name}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1 truncate">{rp.name}</h3>
                   {rp.subCategory === 'COURIER_BAGS' && rp.courierBagType ? (
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          rp.courierBagType === 'POD'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {rp.courierBagType === 'POD' ? 'POD' : 'Non-POD'}
                      </span>
                    ) : rp.subCategory === 'PAPER_BAGS' && rp.paperBagType ? (
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        {rp.paperBagType.replace(/_/g, ' ')}
                      </span>
                    ) : (
                      <span className="text-lg font-bold text-gray-900">
                        ₹{rp.price || rp.sellingPrice || 0}
                      </span>
                    )}
                  </div>
                </Link>
                <div className="px-4 pb-4">
                  <AddToCartButton product={rp} showQuantity={false} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;